import { useQuery } from '@tanstack/react-query';
import { getStaffMetrics } from '../../api/staff';
import { useT, type DictKey } from '../../i18n';
import { formatRubMinor } from '../../lib/staffFormat';

const PLAN_KEY: Record<string, DictKey> = {
  free: 'billing.plan.free',
  pro: 'billing.plan.pro',
  business: 'billing.plan.business',
  enterprise: 'billing.plan.enterprise',
};

// ANX-087f/d — продуктовые метрики + MRR + распределение по планам.
export function StaffDashboard() {
  const t = useT();
  const q = useQuery({ queryKey: ['staff_metrics'], queryFn: getStaffMetrics });

  if (q.isLoading) return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  if (q.isError || !q.data) return <p className="text-sm text-red-600">{t('staff.loadError')}</p>;
  const m = q.data;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">{t('staff.dashboard.title')}</h1>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label={t('staff.metric.mrr')} value={formatRubMinor(m.mrr_rub_minor)} accent />
        <Stat label={t('staff.metric.tenants')} value={String(m.totals.tenants_total)} />
        <Stat label={t('staff.metric.active')} value={String(m.totals.tenants_active)} />
        <Stat label={t('staff.metric.suspended')} value={String(m.totals.tenants_suspended)} />
        <Stat label={t('staff.metric.operators')} value={String(m.totals.operators_total)} />
        <Stat label={t('staff.metric.dialogs30')} value={String(m.totals.conversations_30d)} />
        <Stat label={t('staff.metric.new30')} value={String(m.totals.new_tenants_30d)} />
      </div>

      <section className="rounded-md border border-slate-200 bg-white p-5 shadow-card">
        <h2 className="mb-3 text-sm font-medium text-slate-900">{t('staff.dashboard.byPlan')}</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-1.5">{t('staff.col.plan')}</th>
              <th className="py-1.5 text-right">{t('staff.col.tenants')}</th>
              <th className="py-1.5 text-right">{t('staff.col.activeSubs')}</th>
              <th className="py-1.5 text-right">{t('staff.col.mrrPart')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {m.plans.map((p) => (
              <tr key={p.plan}>
                <td className="py-2 text-slate-900">{t(PLAN_KEY[p.plan] ?? 'billing.plan.free')}</td>
                <td className="py-2 text-right">{p.tenants}</td>
                <td className="py-2 text-right">{p.active_subs}</td>
                <td className="py-2 text-right text-slate-500">
                  {formatRubMinor(p.active_subs * p.price_rub_minor)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={'rounded-md border p-4 shadow-card ' + (accent ? 'border-accent-200 bg-accent-50' : 'border-slate-200 bg-white')}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
